import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { useCreateBillingEntity, getBillingApiError } from '@data/billing/billing'
import { IBillingAddress, ITaxIdData } from '@data/billing/model/billing.model'
import { COUNTRY_CODE_OPTIONS, taxIdTypesForCountry, TaxIdType } from '@data/billing/countryOptions'

interface Props {
  accountId: number
  show: boolean
  onHide: () => void
  defaults?: {
    name?: string
    currency?: 'EUR' | 'GBP' | 'USD'
  }
}

const TAX_ID_LABELS: Record<TaxIdType, string> = {
  eu_vat: 'EU VAT number',
  gb_vat: 'UK VAT number',
  us_ein: 'US Employer Identification Number (EIN)',
}

const emptyAddress: IBillingAddress = {
  line1: '', line2: '', city: '', state: '', postal_code: '', country: '',
}

// PO handling: 0 = No PO, 1 = Constant PO (one fixed reference), 2 = Invoice PO (per-invoice)
type PoMode = 0 | 1 | 2
const PO_MODE_NONE: PoMode = 0
const PO_MODE_CONSTANT: PoMode = 1
const PO_MODE_PER_INVOICE: PoMode = 2

const BillingEntityModal: React.FC<Props> = ({ accountId, show, onHide, defaults }) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [currency, setCurrency] = useState<'EUR' | 'GBP' | 'USD'>('EUR')
  const [individualName, setIndividualName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [cisRegistration, setCisRegistration] = useState('')
  const [poWoReference, setPoWoReference] = useState('')
  const [poMode, setPoMode] = useState<PoMode>(PO_MODE_NONE)
  const [address, setAddress] = useState<IBillingAddress>(emptyAddress)
  const [sameAsShipping, setSameAsShipping] = useState(true)
  const [shippingName, setShippingName] = useState('')
  const [shippingAddress, setShippingAddress] = useState<IBillingAddress>(emptyAddress)
  const [taxIdValue, setTaxIdValue] = useState('')
  const [taxIdError, setTaxIdError] = useState<string | null>(null)
  const { mutate: createEntity, isPending } = useCreateBillingEntity()

  const taxIdType: TaxIdType | undefined = taxIdTypesForCountry(address.country)[0]

  useEffect(() => {
    if (show) {
      setName(defaults?.name ?? '')
      setEmail('')
      setCurrency(defaults?.currency ?? 'EUR')
      setIndividualName('')
      setBusinessName(defaults?.name ?? '')
      setCisRegistration('')
      setPoWoReference('')
      setPoMode(PO_MODE_NONE)
      setAddress(emptyAddress)
      setSameAsShipping(true)
      setShippingName('')
      setShippingAddress(emptyAddress)
      setTaxIdValue('')
      setTaxIdError(null)
    }
  }, [show])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTaxIdError(null)
    const tax_id_data: ITaxIdData[] | undefined =
      taxIdType && taxIdValue ? [{ type: taxIdType, value: taxIdValue }] : undefined

    createEntity(
      {
        account_id: accountId,
        stripe_account_id: currency,
        name,
        email,
        individual_name: individualName || undefined,
        business_name: businessName || undefined,
        description: name,
        address,
        shipping_name: sameAsShipping ? shippingName || name : shippingName || undefined,
        shipping_address: sameAsShipping ? address : shippingAddress,
        cis_registration: cisRegistration || undefined,
        po_mode: poMode,
        po_wo_reference: poMode === PO_MODE_CONSTANT ? poWoReference || undefined : undefined,
        requires_individual_po_per_invoice: poMode === PO_MODE_PER_INVOICE,
        tax_id_data,
      },
      {
        onSuccess: () => {
          onHide()
        },
        onError: (error) => {
          const apiError = getBillingApiError(error)
          if (apiError?.field === 'tax_id') {
            setTaxIdError(apiError.message)
          }
          // Other errors are surfaced as a toast by the mutation hook.
        },
      },
    )
  }

  function renderAddressFields(
    value: IBillingAddress,
    onChange: (next: IBillingAddress) => void,
  ) {
    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label>Address</Form.Label>
          <Form.Control
            value={value.line1 ?? ''}
            onChange={(e) => onChange({ ...value, line1: e.target.value })}
            placeholder="Street address"
          />
        </Form.Group>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>City</Form.Label>
            <Form.Control
              value={value.city ?? ''}
              onChange={(e) => onChange({ ...value, city: e.target.value })}
              placeholder="City"
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>County/State</Form.Label>
            <Form.Control
              value={value.state ?? ''}
              onChange={(e) => onChange({ ...value, state: e.target.value })}
              placeholder="County or State"
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Post Code</Form.Label>
            <Form.Control
              value={value.postal_code ?? ''}
              onChange={(e) => onChange({ ...value, postal_code: e.target.value })}
              placeholder="Postal code"
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Country</Form.Label>
            <Form.Select
              value={value.country ?? ''}
              onChange={(e) => onChange({ ...value, country: e.target.value })}
            >
              <option value="">— Select —</option>
              {COUNTRY_CODE_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label} ({c.code})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>
      </>
    )
  }

  return (
    <Modal show={show} onHide={onHide} centered scrollable size="lg">
      <Form onSubmit={handleSubmit} className="d-flex flex-column" style={{ minHeight: 0 }}>
        <Modal.Header closeButton>
          <Modal.Title>Create Billing Entity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Stripe Account</Form.Label>
            <Form.Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'EUR' | 'GBP' | 'USD')}
            >
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — United Kingdom</option>
              <option value="USD">USD — United States</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Currency cannot be changed after creation.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Customer Name</Form.Label>
            <Form.Control
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Corporation"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="billing@example.com"
            />
            <Form.Text className="text-muted">Used for billing notifications</Form.Text>
          </Form.Group>

          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Individual Name</Form.Label>
              <Form.Control
                value={individualName}
                onChange={(e) => setIndividualName(e.target.value)}
                placeholder="Contact's full name (optional)"
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>Business Name</Form.Label>
              <Form.Control
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Legal business name (optional)"
              />
            </Form.Group>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>PO/WO Reference</Form.Label>
            <Form.Check
              type="radio"
              id="po-mode-none"
              name="po-mode"
              label="No PO"
              checked={poMode === PO_MODE_NONE}
              onChange={() => setPoMode(PO_MODE_NONE)}
            />
            <Form.Check
              type="radio"
              id="po-mode-constant"
              name="po-mode"
              label="Constant PO (one fixed reference for all invoices)"
              checked={poMode === PO_MODE_CONSTANT}
              onChange={() => setPoMode(PO_MODE_CONSTANT)}
            />
            {poMode === PO_MODE_CONSTANT && (
              <Form.Control
                className="mt-2 mb-2"
                value={poWoReference}
                onChange={(e) => setPoWoReference(e.target.value)}
                placeholder="Purchase Order or Work Order reference (optional)"
              />
            )}
            <Form.Check
              type="radio"
              id="po-mode-per-invoice"
              name="po-mode"
              label="Invoice PO (a unique PO number per invoice)"
              checked={poMode === PO_MODE_PER_INVOICE}
              onChange={() => setPoMode(PO_MODE_PER_INVOICE)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>CIS Registration</Form.Label>
            <Form.Control
              value={cisRegistration}
              onChange={(e) => setCisRegistration(e.target.value)}
              placeholder="Construction Industry Scheme registration (optional)"
            />
            <Form.Text className="text-muted">UK Construction Industry Scheme</Form.Text>
          </Form.Group>

          <hr />
          <h6>Billing Address</h6>
          {renderAddressFields(address, setAddress)}

          <Form.Group className="mb-3">
            <Form.Label>{taxIdType ? TAX_ID_LABELS[taxIdType] : 'Tax ID'}</Form.Label>
            <Form.Control
              value={taxIdValue}
              onChange={(e) => {
                setTaxIdValue(e.target.value)
                if (taxIdError) setTaxIdError(null)
              }}
              placeholder="VAT number or Tax ID (optional)"
              disabled={!taxIdType}
              isInvalid={!!taxIdError}
            />
            {taxIdError && (
              <Form.Control.Feedback type="invalid">{taxIdError}</Form.Control.Feedback>
            )}
            {!taxIdType && !taxIdError && (
              <Form.Text className="text-muted">Select a billing country above to enter a tax ID.</Form.Text>
            )}
          </Form.Group>

          <hr />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Delivery Address</h6>
            <Form.Check
              type="checkbox"
              label="Same as billing address"
              checked={sameAsShipping}
              onChange={(e) => setSameAsShipping(e.target.checked)}
            />
          </div>
          {!sameAsShipping && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Delivery Contact Name</Form.Label>
                <Form.Control
                  value={shippingName}
                  onChange={(e) => setShippingName(e.target.value)}
                  placeholder="Delivery Contact Name"
                />
              </Form.Group>
              {renderAddressFields(shippingAddress, setShippingAddress)}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? <Spinner size="sm" animation="border" /> : 'Create Billing Entity'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default BillingEntityModal
